// src/services/cnpj-external/brasilapi/adapter.js
// *******************************************************************************
// BrasilAPI CNPJ Adapter
// *******************************************************************************
// Provider: BrasilAPI (https://brasilapi.com.br)
// Endpoint: GET /api/cnpj/v1/{cnpj}
// Rate Limit: Unlimited (free, no authentication)
// Documentation: https://brasilapi.com.br/docs#tag/CNPJ
// *******************************************************************************

import fetchX from 'utils/fetchX';
import { normalize } from 'utils/helpers';
import {
  createEmptyCnpjPojo,
  createFailureCnpjPojo,
  mapRegistrationStatus,
  parseBrazilianDate,
  cleanPhoneNumber,
  derivePhoneType,
  cleanCnpj,
  cleanCep,
  getIsicSectionFromCnae,
} from '../cnpjUniversalPojo.js';

// Helper to normalize text to Capital Case (defensive: handles null/undefined/non-string)
const toCapital = str => {
  if (str == null) return null;
  if (typeof str !== 'string') return String(str);
  return normalize(str, 'capital');
};

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const PROVIDER_NAME = 'brasilapi';
const BASE_URL = 'https://brasilapi.com.br/api/cnpj/v1';

// *******************************************************************************
// ADAPTER INTERFACE
// *******************************************************************************

/**
 * Searches CNPJ data via BrasilAPI
 *
 * @param {Object} payload - Search parameters
 * @param {string} payload.cnpj - CNPJ number (with or without formatting)
 * @returns {Promise<Object>} Raw API response
 */
async function searchCnpj(payload) {
  const { cnpj } = normalizeRequestPayload(payload);

  const url = `${BASE_URL}/${cnpj}`;

  try {
    const response = await fetchX(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      // BrasilAPI returns 404 for not found, 400 for invalid CNPJ
      if (response.status === 404) {
        return {
          _apiWorked: true,
          _notFound: true,
          _error: 'CNPJ not found',
        };
      }

      if (response.status === 400) {
        return {
          _apiWorked: true,
          _error: 'Invalid CNPJ format',
        };
      }

      return {
        _apiWorked: false,
        _error: `API error: ${response.status} ${response.statusText}`,
      };
    }

    const data = await response.json();
    return {
      ...data,
      _apiWorked: true,
    };
  } catch (error) {
    return {
      _apiWorked: false,
      _error: error.message || 'Network error',
    };
  }
}

/**
 * Normalizes incoming payload
 *
 * @param {Object} payload - Raw payload
 * @returns {Object} Normalized payload
 */
function normalizeRequestPayload(payload) {
  const { cnpj } = payload;
  return {
    cnpj: cleanCnpj(cnpj),
  };
}

/**
 * Normalizes BrasilAPI response to Universal POJO
 *
 * BrasilAPI response structure:
 * {
 *   cnpj: "00000000000191",
 *   razao_social: "BANCO DO BRASIL SA",
 *   nome_fantasia: "BANCO DO BRASIL SA",
 *   data_inicio_atividade: "1966-08-01",
 *   descricao_situacao_cadastral: "ATIVA",
 *   data_situacao_cadastral: "2005-11-03",
 *   motivo_situacao_cadastral: "SEM MOTIVO",
 *   descricao_situacao_especial: null,
 *   data_situacao_especial: null,
 *   cep: "70073901",
 *   ddd_telefone_1: "6132161000",
 *   ddd_telefone_2: null,
 *   tipo_logradouro: "QUADRA",
 *   logradouro: "SAUN Q 5 LOTE B TORRES I,II E III",
 *   numero: "SN",
 *   complemento: "",
 *   bairro: "ASA NORTE",
 *   municipio: "BRASILIA",
 *   uf: "DF",
 *   codigo_municipio: "5300108",
 *   capital_social: 120000000000,
 *   natureza_juridica: "Sociedade de Economia Mista",
 *   codigo_natureza_juridica: "2038",
 *   porte: "DEMAIS",
 *   cnae_fiscal: 6422100,
 *   cnae_fiscal_descricao: "Bancos múltiplos, com carteira comercial",
 *   cnaes_secundarios: [{ codigo: 6493000, descricao: "..." }],
 *   opcao_pelo_simples: true/false,
 *   data_opcao_pelo_simples: "2000-01-01",
 *   data_exclusao_do_simples: null,
 *   opcao_pelo_mei: false,
 *   qsa: [
 *     {
 *       nome_socio: "FULANO DE TAL",
 *       cnpj_cpf_do_socio: "12345678901",
 *       codigo_qualificacao_socio: "10",
 *       qualificacao_socio: "DIRETOR",
 *       data_entrada_sociedade: "2020-01-01",
 *       nome_representante_legal: "...",
 *       cnpj_cpf_do_representante_legal: "...",
 *       qualificacao_representante_legal: "..."
 *     }
 *   ]
 * }
 *
 * @param {Object} response - BrasilAPI response
 * @returns {CnpjUniversalPojo} Universal POJO
 */
function normalizeResponseBody(response) {
  // Handle API failure
  if (!response._apiWorked) {
    return createFailureCnpjPojo({
      provider: PROVIDER_NAME,
      apiWorked: false,
      error: response._error || 'API request failed',
    });
  }

  // Handle CNPJ not found
  if (response._notFound) {
    return createFailureCnpjPojo({
      provider: PROVIDER_NAME,
      apiWorked: true,
      error: 'CNPJ not found in Receita Federal database',
    });
  }

  // Handle other errors
  if (response._error) {
    return createFailureCnpjPojo({
      provider: PROVIDER_NAME,
      apiWorked: true,
      error: response._error,
    });
  }

  // Build Universal POJO from successful response
  const pojo = createEmptyCnpjPojo();

  // Provider metadata
  pojo.provider = PROVIDER_NAME;
  pojo.success = true;
  pojo.apiWorked = true;
  pojo.error = null;
  pojo.source = PROVIDER_NAME;

  // Company names
  pojo.name = toCapital(response.nome_fantasia || response.razao_social) || null;
  pojo.fullName = toCapital(response.razao_social) || null;

  // Dates
  pojo.foundedAt = parseBrazilianDate(response.data_inicio_atividade);

  // Documents - CNPJ
  pojo.documents = [
    {
      documentType: 'cnpj',
      value: cleanCnpj(response.cnpj),
      countryCode: 'BR',
      isPrimary: true,
    },
  ];

  // Contacts - Phones
  // cleanPhoneNumber fixes old mobile numbers missing the "9" prefix
  // derivePhoneType determines the correct type from the corrected number
  pojo.contacts = [];
  if (response.ddd_telefone_1) {
    const cleaned1 = cleanPhoneNumber(response.ddd_telefone_1);
    pojo.contacts.push({
      contactType: 'phone',
      phoneType: derivePhoneType(cleaned1),
      value: cleaned1,
      countryCode: 'BR',
      isPrimary: true,
    });
  }
  if (response.ddd_telefone_2) {
    const cleaned2 = cleanPhoneNumber(response.ddd_telefone_2);
    pojo.contacts.push({
      contactType: 'phone',
      phoneType: derivePhoneType(cleaned2),
      value: cleaned2,
      countryCode: 'BR',
      isPrimary: false,
    });
  }

  // Addresses
  const streetName = response.tipo_logradouro
    ? `${response.tipo_logradouro} ${response.logradouro || ''}`.trim()
    : response.logradouro || '';

  pojo.addresses = [
    {
      addressType: 'main',
      zip: cleanCep(response.cep),
      address1: toCapital(streetName) || '',
      address2: toCapital(response.complemento) || '',
      number: response.numero || '',
      neighborhood: toCapital(response.bairro) || '',
      city: toCapital(response.municipio) || '',
      cityCode: response.codigo_municipio || '',
      region: response.uf || '',
      countryCode: 'BR',
      isPrimary: true,
    },
  ];

  // Activities - Main CNAE
  pojo.activities = [];
  if (response.cnae_fiscal) {
    pojo.activities.push({
      code: String(response.cnae_fiscal),
      description: toCapital(response.cnae_fiscal_descricao) || '',
      isPrimary: true,
    });
  }

  // Activities - Secondary CNAEs
  if (Array.isArray(response.cnaes_secundarios)) {
    response.cnaes_secundarios.forEach(cnae => {
      if (cnae.codigo) {
        pojo.activities.push({
          code: String(cnae.codigo),
          description: toCapital(cnae.descricao) || '',
          isPrimary: false,
        });
      }
    });
  }

  // Holders (QSA - Quadro Societário e Administradores)
  pojo.holders = [];
  if (Array.isArray(response.qsa)) {
    response.qsa.forEach(partner => {
      const holder = {
        name: toCapital(partner.nome_socio) || '',
        cpf: cleanPhoneNumber(partner.cnpj_cpf_do_socio), // Works for both CPF and CNPJ
        qualification: toCapital(partner.qualificacao_socio) || '',
        qualificationCode: partner.codigo_qualificacao_socio || '',
        sinceAt: parseBrazilianDate(partner.data_entrada_sociedade),
      };

      // Legal representative
      if (partner.nome_representante_legal) {
        holder.legalRepresentative = {
          name: toCapital(partner.nome_representante_legal),
          cpf: cleanPhoneNumber(partner.cnpj_cpf_do_representante_legal),
          qualification: toCapital(partner.qualificacao_representante_legal) || '',
        };
      }

      pojo.holders.push(holder);
    });
  }

  // Fiscal data
  pojo.fiscalData = {
    mainCnae: response.cnae_fiscal ? String(response.cnae_fiscal) : null,
    taxRegime: response.opcao_pelo_simples ? 'simplesNacional' : null,
    isMei: response.opcao_pelo_mei === true,
    simplesOptedAt: parseBrazilianDate(response.data_opcao_pelo_simples),
    simplesExcludedAt: parseBrazilianDate(response.data_exclusao_do_simples),
  };

  // Registration status
  pojo.registrationStatus = {
    status: mapRegistrationStatus(response.descricao_situacao_cadastral),
    statusText: toCapital(response.descricao_situacao_cadastral) || null,
    date: parseBrazilianDate(response.data_situacao_cadastral),
    reason: toCapital(response.motivo_situacao_cadastral) || null,
    specialStatus: toCapital(response.descricao_situacao_especial) || null,
    specialStatusDate: parseBrazilianDate(response.data_situacao_especial),
  };

  // Other company fields
  pojo.legalNature = toCapital(response.natureza_juridica) || null;
  pojo.legalNatureCode = response.codigo_natureza_juridica || null;
  pojo.shareCapital = response.capital_social || null;
  pojo.size = toCapital(response.porte) || null;

  // Segment - derive from main CNAE code (ISIC Rev.4 section)
  // BrasilAPI only provides cnae_fiscal, so we derive the section from it
  pojo.segment = getIsicSectionFromCnae(response.cnae_fiscal);

  return pojo;
}

// *******************************************************************************
// EXPORTS
// *******************************************************************************
export default {
  searchCnpj,
  normalizeRequestPayload,
  normalizeResponseBody,
};
